import HiddenInput from "@/components/form/components/HiddenInput";
import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { formatLocaleCurrency } from "@/utils/formatUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { isDefined } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { DatePicker, Form, Modal } from "antd";
import dayjs from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useCreateContractIncrement, useUpdateContractIncrement } from "../../../../common/api/queries";
import { ModalSizes } from "../../../../common/constants";
import { ContractType } from "../../enums";
import { Contract, ContractIncrement, CreateUpdateContractIncrement } from "../../types";

interface Props {
  contract: Contract;
  increment?: ContractIncrement;
  isOpen: boolean;
  onCancel: () => void;
}

export const ContractIncrementModalForm = ({ contract, increment, isOpen, onCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<CreateUpdateContractIncrement>();
  const newBaseAmount = Form.useWatch("newBaseAmount", form);

  const createContractIncrement = useCreateContractIncrement();
  const updateContractIncrement = useUpdateContractIncrement();

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        contractOptimisticLockVersion: contract.optimisticLockVersion,
        optimisticLockVersion: increment?.optimisticLockVersion,
        signDate: increment?.signDate,
        newBaseAmount: increment?.newBaseAmount,
        newInstallmentAmount: increment?.newInstallmentAmount,
      });
    }
  }, [contract.optimisticLockVersion, form, increment, isOpen]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      if (increment) {
        updateContractIncrement.mutate(
          { id1: contract.id, id2: increment.id, object: values },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
          },
        );
      } else {
        createContractIncrement.mutate(
          { id: contract.id, object: values },
          {
            onSuccess: onCancel,
            onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
          },
        );
      }
    });
  };

  const getPreviousBaseAmount = () => {
    if (isDefined(increment?.previousBaseAmount)) {
      return increment?.previousBaseAmount;
    }

    if (contract.type === ContractType.INSURANCE_CONTRACT) {
      return contract.annualPremium;
    } else if (contract.type === ContractType.INVESTMENT_CONTRACT) {
      return contract.prepaidTargetAmount;
    }

    return;
  };

  const previousBaseAmount = getPreviousBaseAmount();

  const getPreviousInstallmentAmount = () => {
    if (isDefined(increment?.previousInstallmentAmount)) {
      return increment?.previousInstallmentAmount;
    }

    if (contract.type === ContractType.INSURANCE_CONTRACT) {
      return contract.partialPremium;
    } else if (contract.type === ContractType.INVESTMENT_CONTRACT) {
      return contract.monthlyPayment;
    }

    return 0;
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      title={increment ? t("contract.titles.updateIncrement") : t("contract.titles.createIncrement")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={createContractIncrement.isPending || updateContractIncrement.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="contractIncrementForm">
        <HiddenInput name="optimisticLockVersion" />
        <HiddenInput name="contractOptimisticLockVersion" />

        <Form.Item
          name="signDate"
          label={t("contract.attrs.incrementSignDate")}
          rules={[
            validations.notNull,
            validations.notFuture,
            validations.notBefore(contract.signDate, t("contract.attrs.signDate")),
          ]}
          {...datePickerFormItemProps}
        >
          <DatePicker {...datePickerStandardProps} maxDate={dayjs()} minDate={dayjs(contract.signDate)} />
        </Form.Item>

        <Form.Item
          name="newBaseAmount"
          label={
            <>
              {contract.type === ContractType.INSURANCE_CONTRACT
                ? t("contract.attrs.annualPremium")
                : t("contract.attrs.prepaidTargetAmount")}
              <span className="sub-header-info margin-left-tiny">{`(${t(increment ? "common.previous" : "common.current")}: ${isDefined(previousBaseAmount) ? formatLocaleCurrency(previousBaseAmount) : "-"})`}</span>
            </>
          }
          rules={
            contract.type === ContractType.INSURANCE_CONTRACT
              ? [validations.notNull, validations.minNumber(0.01)]
              : [validations.minNumber(0)]
          }
        >
          <InputNumber type="decimal" addon="euro" />
        </Form.Item>

        <Form.Item
          name="newInstallmentAmount"
          label={
            <>
              {contract.type === ContractType.INSURANCE_CONTRACT
                ? t("contract.attrs.partialPremium")
                : t("contract.attrs.monthlyPayment")}
              <span className="sub-header-info margin-left-tiny">{`(${t(increment ? "common.previous" : "common.current")}: ${formatLocaleCurrency(getPreviousInstallmentAmount())})`}</span>
            </>
          }
          dependencies={["newBaseAmount"]}
          rules={[
            validations.notNull,
            contract.type === ContractType.INSURANCE_CONTRACT ? validations.minNumber(0.01) : validations.minNumber(0),
            contract.type === ContractType.INSURANCE_CONTRACT
              ? validations.maxNumber(newBaseAmount, t("contract.attrs.annualPremium"))
              : validations.none,
          ]}
        >
          <InputNumber type="decimal" addon="euro" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
